import React, { useEffect, useRef, useState } from 'react';
import { Grid, MenuItem } from '@mui/material';
import CustomFormLabel from '../../components/forms/custom-elements/CustomFormLabel';
import {
  getAllStates,
  getDistrictsByStateCode,
  getULBsByDistrictCode,
  getBlocksByDistrictCode,
  getGPsByBlockCode,
  getVillagesByGPCode,
} from '../../firebase/functions';
import CustomSelect from '../../components/forms/custom-elements/CustomSelect';
import { useSelector } from 'react-redux';

const LGD = ({
  selectedLocation,
  setSelectedLocation,
  selectedStateCode,
  setSelectedStateCode,
  selectedDistrictCode,
  setSelectedDistrictCode,
  selectedBlockCode,
  setSelectedBlockCode,
  selectedULBCode,
  setSelectedULBCode,
  selectedGPCode,
  setSelectedGPCode,
  selectedVillageCode,
  setSelectedVillageCode,
  isDisabled = false,
  // Campaign year of the screen using this picker. Report screens pass the year
  // of the tab being viewed so the codes offered match the codes those events
  // were filed with; data-entry screens leave it undefined and get the current
  // masters.
  eventYear,
}) => {
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [ulbs, setULBs] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [gps, setGPs] = useState([]);
  const [villages, setVillages] = useState([]);
  const profile = useSelector((state) => state.ProfileReducer);

  useEffect(() => {
    if (profile.info?.user_record?.role_id == 5 && profile?.info?.user_details?.length > 0) {
      getStateDistricts(profile?.info?.user_details[0].state_code);
      setSelectedLocation(profile?.info?.user_details[0].location_type);
      setSelectedStateCode(profile?.info?.user_details[0].state_code);
    }
    if (profile.info?.user_record?.role_id == 6 && profile?.info?.user_details?.length > 0) {
      getStateDistricts(profile?.info?.user_details[0].state_code);
      if (profile?.info?.user_details[0].location_type === 'RURAL') {
        getDistrictBlocks(profile?.info?.user_details[0].district_code);
      } else {
        getDistrictULBs(profile?.info?.user_details[0].district_code);
      }
      setSelectedLocation(profile?.info?.user_details[0].location_type);
      setSelectedStateCode(profile?.info?.user_details[0].state_code);
      setSelectedDistrictCode(profile?.info?.user_details[0].district_code);
    }
    if (profile.info?.user_record?.role_id == 7 && profile?.info?.user_details?.length > 0) {
      getStateDistricts(profile?.info?.user_details[0].state_code);
      getDistrictULBs(profile?.info?.user_details[0].district_code);
      setSelectedLocation(profile?.info?.user_details[0].location_type);
      setSelectedStateCode(profile?.info?.user_details[0].state_code);
      setSelectedDistrictCode(profile?.info?.user_details[0].district_code);
      setSelectedULBCode(profile?.info?.user_details[0].ulb_code);
    }
    if (profile.info?.user_record?.role_id == 8 && profile?.info?.user_details?.length > 0) {
      getStateDistricts(profile?.info?.user_details[0].state_code);
      getDistrictBlocks(profile?.info?.user_details[0].district_code);
      getGPs(profile?.info?.user_details[0].block_code);
      getStateDistricts(profile?.info?.user_details[0].state_code);
      getDistrictULBs(profile?.info?.user_details[0].district_code);
      setSelectedLocation(profile?.info?.user_details[0].location_type);
      setSelectedStateCode(profile?.info?.user_details[0].state_code);
      setSelectedDistrictCode(profile?.info?.user_details[0].district_code);
      setSelectedBlockCode(profile?.info?.user_details[0].block_code);
    }
  }, [profile?.info]);

  useEffect(() => {
    if (!selectedLocation || !selectedStateCode) {
      setDistricts([]);
      setULBs([]);
      setBlocks([]);
      setGPs([]);
      setVillages([]);
    }
    if (!selectedDistrictCode) {
      setULBs([]);
      setBlocks([]);
      setGPs([]);
      setVillages([]);
    }
    if (!selectedBlockCode) {
      setULBs([]);
      setGPs([]);
      setVillages([]);
    }
    if (!selectedGPCode) {
      setULBs([]);
      setVillages([]);
    }
  }, [
    selectedLocation,
    selectedStateCode,
    selectedDistrictCode,
    selectedBlockCode,
    selectedGPCode,
  ]);

  // A year change swaps which master the lists come from, so the lists already
  // on screen are stale. Nothing else refetches them: districts are only
  // fetched when the user picks a state, or once from the profile on mount.
  //
  // The district and ULB selections are dropped rather than re-sent, because a
  // code from one year's master does not identify the same place in the
  // other's. State codes are shared, so the state selection survives and its
  // district list is refetched for the new year.
  const previousEventYear = useRef(eventYear);
  useEffect(() => {
    if (previousEventYear.current === eventYear) return;
    previousEventYear.current = eventYear;

    setDistricts([]);
    setULBs([]);
    if (setSelectedDistrictCode) setSelectedDistrictCode('');
    if (setSelectedULBCode) setSelectedULBCode('');

    if (selectedStateCode) getStateDistricts(selectedStateCode);
  }, [eventYear, selectedStateCode]);

  const getStates = async () => {
    const statesData = await getAllStates();
    setStates(
      statesData
        ?.map((o) => {
          return { label: o.state_name, value: o.state_code };
        })
        .sort((a, b) => a.label.localeCompare(b.label))
    );
  };

  const getStateDistricts = async (id) => {
    const districtData = await getDistrictsByStateCode(id, eventYear);

    const uniqueDistricts = districtData
      ?.reduce((acc, curr) => {
        if (!acc.some((item) => item.value === curr.district_code)) {
          acc.push({ label: curr.district_name, value: curr.district_code });
        }
        return acc;
      }, [])
      .sort((a, b) => a.label.localeCompare(b.label));

    setDistricts(uniqueDistricts);
  };

  const getDistrictULBs = async (id) => {
    const ulbsData = await getULBsByDistrictCode(id, eventYear);
    setULBs(
      ulbsData
        ?.map((o) => {
          return { label: o.ulb_name, value: o.ulb_code };
        })
        .sort((a, b) => a.label.localeCompare(b.label))
    );
  };

  const getDistrictBlocks = async (id) => {
    const blocksData = await getBlocksByDistrictCode(id);
    setBlocks(
      blocksData
        ?.map((o) => {
          return { label: o.block_name, value: o.block_code };
        })
        .sort((a, b) => a.label.localeCompare(b.label))
    );
  };

  const getGPs = async (id) => {
    const gpsData = await getGPsByBlockCode(id);
    setGPs(
      gpsData
        ?.map((o) => {
          return { label: o.gp_name, value: o.gp_code };
        })
        .sort((a, b) => a.label.localeCompare(b.label))
    );
  };

  const getVillages = async (id) => {
    const villagesData = await getVillagesByGPCode(id);
    setVillages(
      villagesData
        ?.map((o) => {
          return { label: o.village_name, value: o.village_code };
        })
        .sort((a, b) => a.label.localeCompare(b.label))
    );
  };

  useEffect(() => {
    getStates();
  }, []);

  const handleLocationTypeChange = (name) => {
    setSelectedLocation(name);
    setSelectedStateCode(undefined);
    setSelectedDistrictCode(undefined);
    setSelectedBlockCode(undefined);
    setSelectedGPCode(undefined);
    setSelectedVillageCode(undefined);
    setSelectedULBCode(undefined);
  };

  const handleStateChange = (value) => {
    setSelectedStateCode(value);
    setSelectedDistrictCode(undefined);
    setSelectedBlockCode(undefined);
    setSelectedGPCode(undefined);
    setSelectedVillageCode(undefined);
    setSelectedULBCode(undefined);
    getStateDistricts(value);
  };

  const handleDistrictChange = (value) => {
    setSelectedDistrictCode(value);
    setSelectedBlockCode(undefined);
    setSelectedGPCode(undefined);
    setSelectedVillageCode(undefined);
    setSelectedULBCode(undefined);
    if (selectedLocation === 'URBAN') {
      getDistrictULBs(value);
    } else if (selectedLocation === 'RURAL') {
      getDistrictBlocks(value);
    }
  };

  const handleBlockChange = (value) => {
    setSelectedBlockCode(value);
    setSelectedGPCode(undefined);
    setSelectedVillageCode(undefined);
    setSelectedULBCode(undefined);
    getGPs(value);
  };

  const handleGPChange = (value) => {
    setSelectedGPCode(value);
    setSelectedVillageCode(undefined);
    setSelectedULBCode(undefined);
    getVillages(value);
  };

  const handleVillageChange = (value) => {
    setSelectedVillageCode(value);
    setSelectedULBCode(undefined);
  };

  const handleUlbChange = (value) => {
    setSelectedVillageCode(undefined);
    setSelectedULBCode(value);
    setSelectedGPCode(undefined);
  };

  return (
    <>
      <Grid
        item
        xs={12}
        md={3}
        sx={{
          alignItems: 'flex-start',
          flexDirection: 'column',
          display: 'flex',
        }}
      >
        <CustomFormLabel>Location Type</CustomFormLabel>
        <CustomSelect
          value={selectedLocation || ''}
          onChange={(e) => handleLocationTypeChange(e.target.value)}
          id="location_type"
          name="location_type"
          fullWidth
          size="small"
          disabled={
            profile.info?.user_record?.role_id == 7 ||
            profile.info?.user_record?.role_id == 6 ||
            profile.info?.user_record?.role_id == 8 ||
            profile.info?.user_record?.role_id == 5 ||
            isDisabled
          }
        >
          <MenuItem value="RURAL">RURAL</MenuItem>
          <MenuItem value="URBAN">URBAN</MenuItem>
        </CustomSelect>
      </Grid>
      {states?.length > 0 && (
        <Grid
          item
          xs={12}
          md={3}
          sx={{
            alignItems: 'flex-start',
            flexDirection: 'column',
            display: 'flex',
          }}
        >
          <CustomFormLabel>State Name</CustomFormLabel>
          <CustomSelect
            value={selectedStateCode || ''}
            onChange={(e) => handleStateChange(e.target.value)}
            id="state_code"
            name="state_code"
            fullWidth
            size="small"
            disabled={
              profile.info?.user_record?.role_id == 7 ||
              profile.info?.user_record?.role_id == 6 ||
              profile.info?.user_record?.role_id == 5 ||
              profile.info?.user_record?.role_id == 8
            }
          >
            {states.map((data) => (
              <MenuItem value={data.value} key={data.value}>
                {data.label}
              </MenuItem>
            ))}
          </CustomSelect>
        </Grid>
      )}
      {districts.length > 0 && (
        <Grid
          item
          xs={12}
          md={3}
          sx={{
            alignItems: 'flex-start',
            flexDirection: 'column',
            display: 'flex',
          }}
        >
          <CustomFormLabel>District Name</CustomFormLabel>
          <CustomSelect
            value={selectedDistrictCode || ''}
            onChange={(e) => handleDistrictChange(e.target.value)}
            id="district_code"
            name="district_code"
            fullWidth
            size="small"
            disabled={
              profile.info?.user_record?.role_id == 7 ||
              profile.info?.user_record?.role_id == 6 ||
              profile.info?.user_record?.role_id == 8
            }
          >
            {districts.map((data) => (
              <MenuItem value={data.value} key={data.value}>
                {data.label}
              </MenuItem>
            ))}
          </CustomSelect>
        </Grid>
      )}
      {selectedLocation === 'URBAN' && ulbs.length > 0 && (
        <Grid
          item
          xs={12}
          md={3}
          sx={{
            alignItems: 'flex-start',
            flexDirection: 'column',
            display: 'flex',
          }}
        >
          <CustomFormLabel>ULB Name</CustomFormLabel>
          <CustomSelect
            value={selectedULBCode || ''}
            onChange={(e) => handleUlbChange(e.target.value)}
            id="ulb_code"
            name="ulb_code"
            fullWidth
            size="small"
            disabled={profile.info?.user_record?.role_id == 7}
          >
            {ulbs.map((data) => (
              <MenuItem value={data.value} key={data.value}>
                {data.label}
              </MenuItem>
            ))}
          </CustomSelect>
        </Grid>
      )}
      {selectedLocation === 'RURAL' && blocks.length > 0 && (
        <Grid
          item
          xs={12}
          md={3}
          sx={{
            alignItems: 'flex-start',
            flexDirection: 'column',
            display: 'flex',
          }}
        >
          <CustomFormLabel>Block Name</CustomFormLabel>
          <CustomSelect
            value={selectedBlockCode || ''}
            onChange={(e) => handleBlockChange(e.target.value)}
            id="block_code"
            name="block_code"
            fullWidth
            size="small"
            disabled={profile.info?.user_record?.role_id == 8}
          >
            {blocks.map((data) => {
              return (
                <MenuItem value={data.value} key={data.value}>
                  {data.label}
                </MenuItem>
              );
            })}
          </CustomSelect>
        </Grid>
      )}
      {selectedLocation === 'RURAL' && gps.length > 0 && (
        <Grid
          item
          xs={12}
          md={3}
          sx={{
            alignItems: 'flex-start',
            flexDirection: 'column',
            display: 'flex',
          }}
        >
          <CustomFormLabel>GP Name</CustomFormLabel>
          <CustomSelect
            value={selectedGPCode || ''}
            onChange={(e) => handleGPChange(e.target.value)}
            id="gp_code"
            name="gp_code"
            fullWidth
            size="small"
          >
            {gps.map((data) => {
              return (
                <MenuItem value={data.value} key={data.value}>
                  {data.label}
                </MenuItem>
              );
            })}
          </CustomSelect>
        </Grid>
      )}
      {selectedLocation === 'RURAL' && villages.length > 0 && (
        <Grid
          item
          xs={12}
          md={3}
          sx={{
            alignItems: 'flex-start',
            flexDirection: 'column',
            display: 'flex',
          }}
        >
          <CustomFormLabel>Village Name</CustomFormLabel>
          <CustomSelect
            value={selectedVillageCode || ''}
            onChange={(e) => handleVillageChange(e.target.value)}
            id="village_code"
            name="village_code"
            fullWidth
            size="small"
          >
            {villages.map((data) => {
              return (
                <MenuItem value={data.value} key={data.value}>
                  {data.label}
                </MenuItem>
              );
            })}
          </CustomSelect>
        </Grid>
      )}
    </>
  );
};

export default LGD;
